import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ReportBuilderService } from '../../report-builder.service';
import { CsvExportUtils } from '../../../../shared/utils/csv-export-utils';
import { MomentDateUtils } from '../../../../shared/utils/moment-date-utils';
import { WidgetSizeByDayReportData } from './report-preset.model';
import { DateRangeType } from '../../../../shared/domain/date-range-type.enum';
import { DateRangeHolder } from '../../../../shared/domain/date-range-holder.model';


@Component({
  selector: 'cm-report-presets-grouping-by-days',
  templateUrl: './report-presets-impressions-widget-size-by-day.component.html',
  styleUrls: ['./report-presets-impressions-widget-size-by-day.component.scss']
})
export class ReportPresetsImpressionsWidgetSizeByDayComponent implements AfterViewInit, OnInit {
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
  @ViewChild(MatSort, {static: true}) sort: MatSort;

  MomentDateUtils = MomentDateUtils;

  dataSource = new MatTableDataSource<WidgetSizeByDayReportData>();
  loading = false;

  columns = [];
  days: string[] = [];

  filter = {
    name: '',
    salesRepresentativeEmail: '',
    accountManagerEmail: '',
    nameCtrl: new FormControl(),
  };

  dateSelector = {
    dateFrom: this.MomentDateUtils.getDatesFromRange(DateRangeType.LAST_30_DAYS).dateFrom,
    dateTo: MomentDateUtils.yesterday(),
    maxDate: MomentDateUtils.yesterday(),
    allowedDateRangeTypes: [
      DateRangeType.YESTERDAY,
      DateRangeType.THIS_WEEK,
      DateRangeType.LAST_7_DAYS,
      DateRangeType.LAST_MONTH,
      DateRangeType.LAST_30_DAYS
    ],
    onDateRangeChanged: (event: DateRangeHolder) => {
      this.dateSelector.dateFrom = event.dateFrom;
      this.dateSelector.dateTo = event.dateTo;
      this.refreshData();
    }
  };

  constructor(private service: ReportBuilderService) {
  }

  ngOnInit(): void {
    this.refreshData();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    const customFilterFn = (data: WidgetSizeByDayReportData, filter: string) => {
      const nameFilterOk = !this.filter.name || (
        data.publisherName.toLowerCase().includes(this.filter.name.toLowerCase())
        || (data.salesRepresentative && data.salesRepresentative.toLowerCase().includes(this.filter.name.toLowerCase()))
        || data.widgetSize.toLowerCase().includes(this.filter.name.toLowerCase())
      );
      return nameFilterOk;
    };
    this.dataSource.filterPredicate = customFilterFn;

    this.filter.nameCtrl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged())
      .subscribe(next => {
        this.filter.name = next;
        this.filterData();
      });
  }

  filterData() {
    this.dataSource.filter = <string>(this.filter ? this.filter : '');
  }

  refreshData() {
    this.loading = true;

    this.service.presetImpressionsWidgetSizeByDay(this.dateSelector.dateFrom, this.dateSelector.dateTo, this.filter.salesRepresentativeEmail, this.filter.accountManagerEmail).subscribe(result => {
      // const result = this.data;
      this.loading = false;
      result.data.forEach(data => {
        Object.keys(data.days).forEach(key => {
          data.days[key].ui_ioCpmRpmDiff = (data.days[key].rpm || 0) - (data.days[key].ioCpm || 0);
        });
      });
      this.dataSource.data = result.data;
      this.days = result.days;
      this.columns = ['salesRepresentative', 'publisherName', 'widgetSize'].concat(result.days);
    }, error => {
      this.loading = false;
    });
  }

  exportData() {
    const dataToExport = this.dataSource.data.map(item => {
      const dataObject = {
        'Sales Representative': item.salesRepresentative,
        'Publisher Name': item.publisherName,
        'Widget size': item.widgetSize,
      };

      Object.keys(item.days).forEach(day => {
        dataObject[day + ' Impressions'] = item.days[day].impressions;
        dataObject[day + ' IOCPM'] = item.days[day].ioCpm;
        dataObject[day + ' RPM'] = item.days[day].rpm;
        dataObject[day + ' DIFF'] = item.days[day].ui_ioCpmRpmDiff;
      });

      return dataObject;
    });

    CsvExportUtils.exportCsv(dataToExport, 'Preset report-' + new Date() + '.csv');
  }

  getTotal(date: string, columnName: string) {
    return this.dataSource.data.reduce((sum, currentValue) => {
      const record = currentValue.days[date];

      if (!record) {
        return sum;
      }

      const value = Number(record[columnName]);
      const correctValue = isNaN(value) ? 0 : value;
      return sum + correctValue;
    }, 0);
  }

}
